import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toAiLanguageName } from "../i18n/aiLanguages";
import { executeAiAction } from "../services/aiService";

const EMPTY_STATE = {
  isVisible: false,
  position: { x: 0, y: 0 },
  sourceText: "",
  translatedText: "",
  isLoading: false,
  error: null,
};

const ANCHOR_MARGIN = 12;

/**
 * Preferred popover anchor from the visible part of a selection.
 * Tall selections can extend far outside the viewport — clamp to what's on screen.
 */
export const buildTranslationAnchor = (selectionRect, fallbackPosition = {}) => {
  if (selectionRect) {
    return {
      x: Math.max(ANCHOR_MARGIN, Number(selectionRect.left) || ANCHOR_MARGIN),
      y:
        Math.min(
          Number(selectionRect.bottom) || ANCHOR_MARGIN,
          window.innerHeight - ANCHOR_MARGIN,
        ) + 8,
      anchorTop: Math.max(
        Number(selectionRect.top) || ANCHOR_MARGIN,
        ANCHOR_MARGIN,
      ),
    };
  }

  const x = Number(fallbackPosition.x) || ANCHOR_MARGIN;
  const y = Number(fallbackPosition.y) || ANCHOR_MARGIN;
  return {
    x,
    y: y + 40,
    anchorTop: y,
  };
};

/**
 * Shared selection → translate flow for PDF/EPUB viewers.
 */
const useSelectionTranslation = () => {
  const { t } = useTranslation();
  const [translationState, setTranslationState] = useState(EMPTY_STATE);
  const requestIdRef = useRef(0);

  const closeTranslationPopover = useCallback(() => {
    requestIdRef.current += 1;
    setTranslationState(EMPTY_STATE);
  }, []);

  const runTranslation = useCallback(
    async ({ text, uiLanguageCode, position }) => {
      const source = String(text || "").trim();
      if (!source) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setTranslationState({
        isVisible: true,
        position: position || { x: ANCHOR_MARGIN, y: ANCHOR_MARGIN },
        sourceText: source,
        translatedText: "",
        isLoading: true,
        error: null,
      });

      try {
        const data = await executeAiAction({
          action_type: "translation",
          content: source,
          target_language: toAiLanguageName(uiLanguageCode),
        });
        if (requestIdRef.current !== requestId) return;

        const translated = String(data?.translated_text || "").trim();
        setTranslationState((prev) => ({
          ...prev,
          sourceText: data?.source_text || source,
          translatedText: translated,
          isLoading: false,
          error: translated
            ? null
            : t("contextMenu.translationFailed", "Translation failed"),
        }));
      } catch (error) {
        if (requestIdRef.current !== requestId) return;
        setTranslationState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error?.message ||
            t("contextMenu.translationFailed", "Translation failed"),
        }));
      }
    },
    [t],
  );

  return {
    translationState,
    closeTranslationPopover,
    runTranslation,
  };
};

export default useSelectionTranslation;
